import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { gsap, Power1, Power2 } from 'gsap'

import MainBackground from '../../components/MainBackground'
import withMemo from '../../decorators/withMemo'
import { sizes } from '../../theme'
import { useWindowSize } from '../../hooks/useWindowSize'
import { Link } from '../../components'

import * as SC from './styled'

export type FinalScreenTemplateProps = {
  back?: string
  imageRenditions?: {
    portrait: string
    landscape: string
  }
  onRestartClick: () => void
}

const FinalScreenTemplate: FC<FinalScreenTemplateProps> = ({
  imageRenditions,
  back = 'BACK',
  onRestartClick,
}) => {
  const $loader = useRef(null)
  const $loaderBackground = useRef(null)
  const $loaderImage = useRef(null)

  // state
  const [isImageLoaded, setImageLoaded] = useState(false)

  const { width } = useWindowSize()

  // memo

  const downloadLink = useMemo(() => {
    if (!imageRenditions) {
      return '#'
    } else {
      return width < sizes.tablet ? imageRenditions.portrait : imageRenditions.landscape
    }
  }, [imageRenditions, width])

  // animation

  const animateLoaderOut = useCallback(() => {
    const tl = gsap.timeline()

    tl.set($loader.current, {
      boxShadow: 'none',
      backgroundColor: 'transparent',
    })
      .to($loaderImage.current, {
        duration: 0.2,
        opacity: 0,
        ease: Power1.easeOut,
      })
      .to(
        $loaderBackground.current,
        {
          scaleX: 0,
          duration: 0.5,
          ease: Power2.easeInOut,
        },
        0.2
      )
  }, [])

  // handlers

  const handleOnImageLoad = useCallback(() => {
    setImageLoaded(true)
    animateLoaderOut()
  }, [animateLoaderOut])

  // return

  return (
    <SC.FinalScreenTemplate>
      <MainBackground />
      {imageRenditions && (
        <SC.Photo isImageLoaded={isImageLoaded}>
          <img src={imageRenditions.portrait} alt="Photo" onLoad={handleOnImageLoad} />
        </SC.Photo>
      )}
      <SC.PhotoLoader ref={$loader}>
        <SC.PhotoLoaderRatioBox>
          <SC.PhotoLoaderRatioContent>
            <SC.PhotoLoaderBackground ref={$loaderBackground} />
            <img src={'/static/images/loader.gif'} alt={'loader'} ref={$loaderImage} />
          </SC.PhotoLoaderRatioContent>
        </SC.PhotoLoaderRatioBox>
      </SC.PhotoLoader>

      <SC.ButtonsContainer>
        <SC.Btn text={back} onClick={onRestartClick} />
        <SC.SaveLink link={downloadLink} download="love-balenciaga" target={'_self'}>
          <SC.Btn text="SAVE" />
        </SC.SaveLink>
        <Link link="https://www.balenciaga.com/us/all/i-love-u">
          <SC.Btn text="SHOP" />
        </Link>
      </SC.ButtonsContainer>
    </SC.FinalScreenTemplate>
  )
}

export default withMemo(FinalScreenTemplate)
